import { useEffect, useRef, useState } from 'react';
import { shortMonthDay } from '../lib/dates';

interface Props {
  open: boolean;
  group: string | null;
  startDate: string;
  endDate: string;
  initialItems: string[];
  catalog: string[];
  anchor: { x: number; y: number } | null;
  onClose: () => void;
  onApply: (items: string[]) => void;
  onClear: () => void;
}

export function ItemPicker(props: Props) {
  const { open, group, startDate, endDate, initialItems, catalog, anchor, onClose, onApply, onClear } = props;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [custom, setCustom] = useState('');
  const [extras, setExtras] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(new Set(initialItems));
      setCustom('');
      setExtras([]);
    }
  }, [open, initialItems]);

  // Dialog focus 管理：開啟時把 focus 移進 popover，關閉時歸還原本的元素（例：格子）
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => ref.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Focus trap：Tab / Shift+Tab 在 popover 內循環，不跑出去
    const tabHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current) return;
      const focusables = Array.from(
        ref.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active !== null && ref.current.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) { e.preventDefault(); last.focus(); }
      } else {
        if (!inside || active === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    document.addEventListener('keydown', tabHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
      document.removeEventListener('keydown', tabHandler);
    };
  }, [open, onClose]);

  if (!open || !anchor) return null;

  const toggle = (item: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };
  const addCustom = () => {
    const val = custom.trim();
    if (!val) return;
    setExtras(prev => prev.includes(val) ? prev : [...prev, val]);
    setSelected(prev => new Set(prev).add(val));
    setCustom('');
  };

  const apply = () => {
    onApply(Array.from(selected));
  };

  const allOptions = [...catalog, ...extras.filter(e => !catalog.includes(e))];
  const rangeLabel = startDate === endDate
    ? shortMonthDay(startDate)
    : `${shortMonthDay(startDate)} – ${shortMonthDay(endDate)}`;

  // Position popover within viewport（小視窗縮到 100vw-16、x/y clamp 不爆版）
  const POP_W = Math.min(360, window.innerWidth - 16);
  const POP_H = 420;
  const x = Math.max(8, Math.min(anchor.x, window.innerWidth - POP_W - 8));
  const y = Math.max(8, Math.min(anchor.y, window.innerHeight - POP_H - 8));

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={`${group ?? ''} ${rangeLabel} 工項選擇`}
      tabIndex={-1}
      className="fixed z-50 bg-tb-surface rounded-lg shadow-xl border border-tb-border p-3 focus:outline-none"
      style={{ left: x, top: y, width: POP_W, maxHeight: POP_H }}
    >
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-tb-surface2">
        <div className="text-xs text-tb-text2">
          <span className="font-semibold text-tb-text">{group ?? ''}</span>
          <span className="mx-1">·</span>
          <span>{rangeLabel}</span>
        </div>
        <button onClick={onClose} aria-label="關閉" className="text-tb-text3 hover:text-tb-text text-sm">✕</button>
      </div>

      <div className="grid grid-cols-3 gap-1 mb-2 max-h-56 overflow-y-auto">
        {allOptions.map(item => (
          <button
            key={item}
            onClick={() => toggle(item)}
            className={[
              'text-xs px-2 py-1.5 rounded border transition',
              selected.has(item)
                ? 'bg-brand-yellow text-brand-black border-brand-yellow font-semibold'
                : 'bg-tb-bg text-tb-text border-tb-border hover:bg-tb-surface2',
            ].join(' ')}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="flex gap-1 mb-2">
        <input
          className="input flex-1 text-xs"
          placeholder="自訂工項…例：木工收尾"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
        />
        <button
          onClick={addCustom}
          className="text-xs px-3 py-1.5 rounded border border-tb-border bg-tb-surface hover:bg-tb-bg"
        >+ 加入</button>
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-tb-surface2">
        <button
          onClick={() => { onClear(); onClose(); }}
          className="text-xs px-3 py-1.5 rounded border border-tb-rust-border text-tb-rust hover:bg-tb-rust-bg"
        >清空此範圍</button>
        <div className="flex gap-1">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-tb-border text-tb-text2 hover:bg-tb-bg"
          >取消</button>
          <button
            onClick={apply}
            className="text-xs px-3 py-1.5 rounded bg-brand-yellow text-brand-black font-semibold hover:bg-brand-yellow-dark"
          >套用</button>
        </div>
      </div>
    </div>
  );
}
